// skeletons.jsx - shape-matched loading placeholders for the busiest screens.
//
// A spinner says "wait"; a skeleton says "this is what is coming, and where".
// Content then appears in place instead of the page jumping from a centred
// spinner to a full layout. Each skeleton mirrors the real screen's structure
// (header, toolbar, rows or columns) closely enough that nothing shifts much
// when data arrives.
//
// Built on ui.jsx <Skeleton>, so light/dark colours and the shimmer (which
// stops under prefers-reduced-motion) come for free. Every skeleton is a
// role="status" region with an aria-label, so screen readers announce loading
// once instead of reading a pile of empty boxes.
import { Skeleton } from "./ui";
import { C } from "../lib/tokens";

function Busy({ label, children, style }) {
  return (
    <div role="status" aria-busy="true" aria-label={label} style={style}>
      {children}
    </div>
  );
}

const card = { background: C.bgPrimary, border: "0.5px solid " + C.borderLight, borderRadius: 12 };

// Generic routed page: title, a row of stat cards, then a list.
export function PageSkeleton({ label = "Loading page" }) {
  return (
    <Busy label={label} style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      <Skeleton w={220} h={24} r={8} />
      <Skeleton w={320} h={12} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 6 }}>
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} h={88} r={12} />)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={52} r={8} />)}
      </div>
    </Busy>
  );
}

// Patient chart: identity header card, tab strip, two content columns.
export function ChartSkeleton() {
  return (
    <Busy label="Loading patient chart" style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ ...card, padding: 16, display: "flex", gap: 14, alignItems: "center" }}>
        <Skeleton w={52} h={52} r={999} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <Skeleton w={220} h={20} r={6} />
          <Skeleton w={300} h={12} />
          <div style={{ display: "flex", gap: 6 }}>
            {[70, 90, 60].map((w, i) => <Skeleton key={i} w={w} h={18} r={999} />)}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[84, 96, 72, 110, 80, 90].map((w, i) => <Skeleton key={i} w={w} h={28} r={8} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
        {[0, 1].map((col) => (
          <div key={col} style={{ ...card, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <Skeleton w={140} h={14} />
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 10 }}>
                <Skeleton h={11} />
                <Skeleton h={11} w={i % 2 ? "70%" : "90%"} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </Busy>
  );
}

// Row cards (avatar, two text lines, status pill). Shared by the worklist and
// ListSkeleton.
function RowStack({ rows }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ ...card, padding: "10px 12px", display: "flex", gap: 12, alignItems: "center" }}>
          <Skeleton w={32} h={32} r={999} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton w={i % 3 === 0 ? "45%" : "35%"} h={13} />
            <Skeleton w={i % 2 ? "60%" : "70%"} h={11} />
          </div>
          <Skeleton w={72} h={20} r={999} />
        </div>
      ))}
    </div>
  );
}

// A list area inside a page that already drew its own header and filters
// (Patients, Recall, Campaigns, Billing queue, Inbound SMS).
export function ListSkeleton({ rows = 6, label = "Loading list" }) {
  return (
    <Busy label={label}>
      <RowStack rows={rows} />
    </Busy>
  );
}

// Care management worklist: header, filter pills, member rows.
export function WorklistSkeleton({ rows = 7 }) {
  return (
    <Busy label="Loading your worklist" style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <Skeleton w={200} h={20} r={6} />
        <Skeleton w={120} h={28} r={8} />
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {[64, 88, 72, 96, 70].map((w, i) => <Skeleton key={i} w={w} h={24} r={999} />)}
      </div>
      <RowStack rows={rows} />
    </Busy>
  );
}

// Schedule: toolbar, then a time column and provider columns with blocks.
export function ScheduleSkeleton({ columns = 3 }) {
  const blocks = [[1, 2], [4, 3], [8, 2], [11, 4]];
  return (
    <Busy label="Loading schedule" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Skeleton w={96} h={30} r={8} />
        <Skeleton w={160} h={30} r={8} />
        <div style={{ flex: 1 }} />
        {[70, 70, 90].map((w, i) => <Skeleton key={i} w={w} h={30} r={8} />)}
      </div>
      <div style={{ ...card, display: "grid", gridTemplateColumns: "64px repeat(" + columns + ", 1fr)", overflow: "hidden" }}>
        <div style={{ borderRight: "0.5px solid " + C.borderLight, padding: 8, display: "flex", flexDirection: "column", gap: 34 }}>
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} w={40} h={10} />)}
        </div>
        {Array.from({ length: columns }).map((_, c) => (
          <div key={c} style={{ position: "relative", minHeight: 360, borderRight: c < columns - 1 ? "0.5px solid " + C.borderLight : "none" }}>
            <div style={{ padding: 8 }}><Skeleton w="60%" h={14} /></div>
            {blocks.map(([top, h], i) => (
              (i + c) % 3 === 2 ? null : (
                <div key={i} style={{ position: "absolute", left: 6, right: 6, top: 30 + top * 22 }}>
                  <Skeleton h={h * 22 - 4} r={6} />
                </div>
              )
            ))}
          </div>
        ))}
      </div>
    </Busy>
  );
}

// Queue, floor view: a grid of room tiles.
export function QueueFloorSkeleton({ tiles = 8 }) {
  return (
    <Busy label="Loading the floor" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <Skeleton w={180} h={22} r={6} />
        <div style={{ flex: 1 }} />
        <Skeleton w={120} h={28} r={8} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
        {Array.from({ length: tiles }).map((_, i) => (
          <div key={i} style={{ ...card, padding: 12, display: "flex", flexDirection: "column", gap: 8, minHeight: 110 }}>
            <Skeleton w="50%" h={13} />
            <Skeleton w="75%" h={11} />
            <div style={{ flex: 1 }} />
            <Skeleton w={80} h={18} r={999} />
          </div>
        ))}
      </div>
    </Busy>
  );
}

// Queue, board view: status lanes with patient cards.
export function QueueBoardSkeleton({ lanes = 4 }) {
  return (
    <Busy label="Loading the queue" style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(" + lanes + ", minmax(200px, 1fr))", gap: 12, overflowX: "auto" }}>
      {Array.from({ length: lanes }).map((_, l) => (
        <div key={l} style={{ background: C.bgSecondary, borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <Skeleton w="45%" h={13} />
          {Array.from({ length: 3 - (l % 2) }).map((_, i) => (
            <div key={i} style={{ ...card, padding: 10, display: "flex", gap: 10, alignItems: "center" }}>
              <Skeleton w={28} h={28} r={999} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                <Skeleton w="70%" h={12} />
                <Skeleton w="45%" h={10} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </Busy>
  );
}
